/**
 * Webshop Store
 *
 * Manages webshop fulfillment state with 2-minute polling.
 * Flow: Klaarmaken verzending → Ingepakt → Verzonden (by MyParcel webhook)
 */

import { create } from 'zustand';
import { salesforceClient } from '../lib/salesforce';
import type { WebshopOrder, WebshopOrderStatus } from '../lib/salesforce';
import { supabase } from '../lib/supabase';

/**
 * POST naar een office-app webshop-endpoint met het Supabase-token. Gooit met
 * de servermelding zodat de pagina hem toont; 18-09-2026 verving dit de rauwe
 * SF-PATCH via /api/sf-proxy die na de cut stil faalde.
 */
async function postWebshop<T = { ok: boolean }>(path: string, body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new Error(data.error || `${path} mislukt (${response.status})`);
  }
  return data;
}

// ============================================================================
// Mock data for local development (SF proxy not available)
// ============================================================================

const MOCK_ORDERS: WebshopOrder[] = [
  {
    id: 'mock-001',
    name: 'WO-10001',
    status: 'Klaarmaken verzending',
    amount: 24.95,
    customerName: 'Jan de Vries',
    customerEmail: 'jan@example.nl',
    customerPhone: '06-12345678',
    street: 'Kerkstraat 12',
    postalCode: '3511AB',
    city: 'Utrecht',
    country: 'NL',
    trackingNumber: null,
    trackingUrl: null,
    shippingParcelId: null,
    shippingStatus: null,
    notes: 'Graag voor 17:00 verzenden',
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_001',
    mollieStatus: 'paid',
    createdDate: new Date().toISOString(),
    items: [
      { id: 'item-001', name: 'Vlekkenbaas 400ml', quantity: 1, price: 14.95, width: null, length: null },
      { id: 'item-002', name: 'James Vlekkenwonder', quantity: 1, price: 10.00, width: null, length: null },
    ],
  },
  {
    id: 'mock-002',
    name: 'WO-10002',
    status: 'Klaarmaken verzending',
    amount: 549.00,
    customerName: 'Piet Janssen',
    customerEmail: 'piet@example.nl',
    customerPhone: '06-87654321',
    street: 'Dorpsweg 45',
    postalCode: '7901AA',
    city: 'Hoogeveen',
    country: 'NL',
    trackingNumber: '3SDEVC0001234',
    trackingUrl: 'https://myparcel.me/track-trace/3SDEVC0001234/1234AB/NL',
    shippingParcelId: 'prcl_new001',
    shippingStatus: 'Aangemeld',
    notes: null,
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_002',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 3600000).toISOString(),
    items: [
      { id: 'item-003', name: 'Ondertapijt op maat', quantity: 1, price: 549.00, width: 200, length: 300 },
    ],
  },
  {
    id: 'mock-003',
    name: 'WO-10003',
    status: 'Klaarmaken verzending',
    amount: 39.85,
    customerName: 'Marie van den Berg',
    customerEmail: 'marie@example.nl',
    customerPhone: '06-11223344',
    street: 'Hoofdstraat 8',
    postalCode: '1012AA',
    city: 'Amsterdam',
    country: 'NL',
    trackingNumber: '3SDEVC0001235',
    trackingUrl: 'https://myparcel.me/track-trace/3SDEVC0001235/1234AB/NL',
    shippingParcelId: 'prcl_new002',
    shippingStatus: 'Aangemeld',
    notes: null,
    fulfillmentType: 'Eigen verzending',
    // Geen molliePaymentId — testorder zonder Mollie-betaling
    molliePaymentId: null,
    mollieStatus: null,
    createdDate: new Date(Date.now() - 86400000 * 2).toISOString(),
    items: [
      { id: 'item-004', name: 'Vlekkenbaas 400ml', quantity: 2, price: 29.90, width: null, length: null },
      { id: 'item-005', name: 'Microvezeldoek set (3st)', quantity: 1, price: 9.95, width: null, length: null },
    ],
  },
  {
    id: 'mock-004',
    name: 'WO-09998',
    status: 'Ingepakt',
    amount: 14.95,
    customerName: 'Kees Bakker',
    customerEmail: 'kees@example.nl',
    customerPhone: '06-99887766',
    street: 'Langestraat 22',
    postalCode: '8011NB',
    city: 'Zwolle',
    country: 'NL',
    trackingNumber: '3SDEVC1234567',
    trackingUrl: 'https://myparcel.me/track-trace/3SDEVC1234567/1234AB/NL',
    shippingParcelId: 'prcl_abc123',
    shippingStatus: 'Aangemeld',
    notes: null,
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_004',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 86400000).toISOString(),
    items: [
      { id: 'item-006', name: 'Vlekkenbaas 400ml', quantity: 1, price: 14.95, width: null, length: null },
    ],
  },
  {
    id: 'mock-005',
    name: 'WO-09997',
    status: 'Ingepakt',
    amount: 74.85,
    customerName: 'Sanne de Groot',
    customerEmail: 'sanne@example.nl',
    customerPhone: null,
    street: 'Bergweg 7',
    postalCode: '3037EJ',
    city: 'Rotterdam',
    country: 'NL',
    trackingNumber: '3SDEVC7654321',
    trackingUrl: 'https://myparcel.me/track-trace/3SDEVC7654321/1234AB/NL',
    shippingParcelId: 'prcl_def456',
    shippingStatus: 'Aangemeld',
    notes: 'Tweede verdieping, geen lift',
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_005',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 86400000 * 2).toISOString(),
    items: [
      { id: 'item-007', name: 'James Vlekkenwonder', quantity: 3, price: 30.00, width: null, length: null },
      { id: 'item-008', name: 'Vlekkenbaas 400ml', quantity: 1, price: 14.95, width: null, length: null },
      { id: 'item-009', name: 'Ondertapijt antislip', quantity: 1, price: 29.90, width: 160, length: 230 },
    ],
  },
  {
    id: 'mock-006',
    name: 'WO-09990',
    status: 'Verzonden',
    amount: 29.90,
    customerName: 'Tom Hendriks',
    customerEmail: 'tom@example.nl',
    customerPhone: '06-55443322',
    street: 'Stationsplein 3',
    postalCode: '6811KS',
    city: 'Arnhem',
    country: 'NL',
    trackingNumber: '3SDEVC9999999',
    trackingUrl: 'https://myparcel.me/track-trace/3SDEVC9999999/1234AB/NL',
    shippingParcelId: 'prcl_ghi789',
    shippingStatus: 'Onderweg',
    notes: null,
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_006',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 86400000 * 3).toISOString(),
    items: [
      { id: 'item-010', name: 'Vlekkenbaas 400ml', quantity: 2, price: 29.90, width: null, length: null },
    ],
  },
  {
    id: 'mock-007',
    name: 'WO-09985',
    status: 'Verzonden',
    amount: 14.95,
    customerName: 'Lisa Smit',
    customerEmail: 'lisa@example.nl',
    customerPhone: '06-11112222',
    street: 'Parkweg 19',
    postalCode: '5611AA',
    city: 'Eindhoven',
    country: 'NL',
    trackingNumber: '3SDEVC8888888',
    trackingUrl: 'https://myparcel.me/track-trace/3SDEVC8888888/1234AB/NL',
    shippingParcelId: 'prcl_jkl012',
    shippingStatus: 'Bezorgd',
    notes: null,
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_007',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 86400000 * 5).toISOString(),
    items: [
      { id: 'item-011', name: 'James Vlekkenwonder', quantity: 1, price: 14.95, width: null, length: null },
    ],
  },
  {
    id: 'mock-bij-lev-001',
    name: 'WO-10100',
    status: 'Bij leverancier',
    amount: 549.00,
    customerName: 'Eva Visser',
    customerEmail: 'eva@example.nl',
    customerPhone: '06-77665544',
    street: 'Schoolstraat 14',
    postalCode: '2611AB',
    city: 'Delft',
    country: 'NL',
    trackingNumber: null,
    trackingUrl: null,
    shippingParcelId: null,
    shippingStatus: null,
    notes: null,
    fulfillmentType: 'Dropship',
    dropshipDoorgestuurdDatum: new Date(Date.now() - 86400000 * 3).toISOString(),
    dropshipBron: 'Office-App',
    molliePaymentId: 'tr_mock_pay_lev_001',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 86400000 * 4).toISOString(),
    items: [
      { id: 'item-lev-1', name: 'SEBO D4 Premium', quantity: 1, price: 549.00, width: null, length: null, dropship: true, dropshipPartnerName: 'SEBO Nederland' },
    ],
  },
  {
    id: 'mock-gemengd-001',
    name: 'WO-10110',
    status: 'Klaarmaken verzending',
    amount: 131.86,
    customerName: 'Solke Munneke',
    customerEmail: 'solkemunneke@example.nl',
    customerPhone: '06-48478090',
    street: 'Westerdok 102',
    postalCode: '1013BH',
    city: 'Amsterdam',
    country: 'NL',
    trackingNumber: '3SMYPA957254857',
    trackingUrl: null,
    shippingParcelId: 'parcel-mock-gemengd',
    shippingStatus: 'Aangemeld',
    notes: null,
    fulfillmentType: 'Gemengd',
    dropshipDoorgestuurdDatum: null,
    dropshipBron: null,
    molliePaymentId: 'tr_mock_pay_gemengd',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 3600000 * 2).toISOString(),
    items: [
      { id: 'item-gem-1', name: 'SEBO Stofzuigerzakken D-serie (8 stuks)', quantity: 4, price: 71.96, width: null, length: null, dropship: true, dropshipPartnerName: 'SEBO Nederland' },
      { id: 'item-gem-2', name: 'James Starterset Tapijt en Meubelstof', quantity: 1, price: 29.95, width: null, length: null, dropship: false },
      { id: 'item-gem-3', name: 'Vlekkenbaas 2-pack (2× 400ml)', quantity: 1, price: 29.95, width: null, length: null, dropship: false },
    ],
  },
  {
    id: 'mock-008',
    name: 'WO-09970',
    status: 'Terugbetaald',
    amount: 24.95,
    customerName: 'Anna Berg',
    customerEmail: 'anna@example.nl',
    customerPhone: '06-22334455',
    street: 'Marktplein 5',
    postalCode: '4811XX',
    city: 'Breda',
    country: 'NL',
    trackingNumber: null,
    trackingUrl: null,
    shippingParcelId: null,
    shippingStatus: null,
    notes: '[01-05-2026 14:30] ANNULERING via Office-App\nStatus was: Klaarmaken verzending\nNieuwe status: Terugbetaald\nRefund: ja, € 24.95 (re_mock_001)\nReden: Klant heeft per ongeluk besteld',
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: 'tr_mock_pay_008',
    mollieStatus: 'paid',
    createdDate: new Date(Date.now() - 86400000 * 4).toISOString(),
    items: [
      { id: 'item-012', name: 'Vlekkenbaas 400ml', quantity: 1, price: 14.95, width: null, length: null },
      { id: 'item-013', name: 'James Vlekkenwonder', quantity: 1, price: 10.00, width: null, length: null },
    ],
  },
  {
    id: 'mock-009',
    name: 'WO-09965',
    status: 'Geannuleerd',
    amount: 0.01,
    customerName: 'Test Order (geen refund)',
    customerEmail: 'test@cincocleaning.com',
    customerPhone: null,
    street: 'Teststraat 1',
    postalCode: '1000AA',
    city: 'Amsterdam',
    country: 'NL',
    trackingNumber: null,
    trackingUrl: null,
    shippingParcelId: null,
    shippingStatus: null,
    notes: '[02-05-2026 10:15] ANNULERING via Office-App\nStatus was: Klaarmaken verzending\nNieuwe status: Geannuleerd\nRefund: nee\nReden: Testorder',
    fulfillmentType: 'Eigen verzending',
    molliePaymentId: null,
    mollieStatus: null,
    createdDate: new Date(Date.now() - 86400000 * 6).toISOString(),
    items: [
      { id: 'item-014', name: 'Test product', quantity: 1, price: 0.01, width: null, length: null },
    ],
  },
];

interface OrderCounts {
  klaarmakenVerzending: number;
  ingepakt: number;
  bijLeverancier: number;
  verzonden: number;
  terugbetaald: number;
  geannuleerd: number; // som van Geannuleerd + Terugbetaald (toon in Geannuleerd-tab)
}

interface WebshopState {
  orders: WebshopOrder[];
  selectedOrderId: string | null;
  activeTab: WebshopOrderStatus;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  lastUpdated: Date | null;
  pollingIntervalId: ReturnType<typeof setInterval> | null;

  // Label printing
  selectedForPrintIds: Set<string>;
  labelStartPosition: number; // 1-4, row-first (1=TL, 2=TR, 3=BL, 4=BR)

  // Computed
  orderCounts: OrderCounts;
  filteredOrders: WebshopOrder[];
  selectedOrder: WebshopOrder | null;

  // Actions
  fetchOrders: () => Promise<void>;
  selectOrder: (id: string | null) => void;
  setActiveTab: (status: WebshopOrderStatus) => void;
  markAsPacked: (orderId: string) => Promise<void>;
  markAllAsPacked: () => Promise<void>;
  createShipment: (orderId: string) => Promise<void>;
  forwardOrder: (orderId: string, to: string, subject: string, body: string) => Promise<void>;
  markVerzondenViaDropship: (orderId: string) => Promise<void>;
  updateAddress: (orderId: string, payload: { street: string; postalCode: string; city: string; countryCode: string }) => Promise<void>;
  cancelOrder: (orderId: string, payload: { reden: string; refund: boolean }) => Promise<void>;
  refresh: () => Promise<void>;

  // Label printing actions
  toggleSelectForPrint: (id: string) => void;
  selectAllForPrint: () => void;
  clearPrintSelection: () => void;
  setLabelStartPosition: (position: number) => void;

  // Polling
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

function computeCounts(orders: WebshopOrder[]): OrderCounts {
  const terugbetaald = orders.filter((o) => o.status === 'Terugbetaald').length;
  const geannuleerd = orders.filter((o) => o.status === 'Geannuleerd').length;
  return {
    klaarmakenVerzending: orders.filter((o) => o.status === 'Klaarmaken verzending').length,
    ingepakt: orders.filter((o) => o.status === 'Ingepakt').length,
    bijLeverancier: orders.filter((o) => o.status === 'Bij leverancier').length,
    verzonden: orders.filter((o) => o.status === 'Verzonden').length,
    terugbetaald,
    geannuleerd: terugbetaald + geannuleerd,
  };
}

function filterForTab(orders: WebshopOrder[], activeTab: WebshopOrderStatus): WebshopOrder[] {
  // De Geannuleerd-tab toont zowel Geannuleerd als Terugbetaald — beide zijn
  // afgehandelde annuleringen, gebruiker hoeft niet over de sub-status na te denken.
  if (activeTab === 'Geannuleerd') {
    return orders.filter((o) => o.status === 'Geannuleerd' || o.status === 'Terugbetaald');
  }
  return orders.filter((o) => o.status === activeTab);
}

function recompute(orders: WebshopOrder[], activeTab: WebshopOrderStatus, selectedOrderId: string | null) {
  return {
    orders,
    orderCounts: computeCounts(orders),
    filteredOrders: filterForTab(orders, activeTab),
    selectedOrder: selectedOrderId ? orders.find((o) => o.id === selectedOrderId) || null : null,
  };
}

export const useWebshopStore = create<WebshopState>((set, get) => ({
  orders: [],
  selectedOrderId: null,
  activeTab: 'Klaarmaken verzending',
  isLoading: false,
  isUpdating: false,
  error: null,
  lastUpdated: null,
  pollingIntervalId: null,

  // Label printing
  selectedForPrintIds: new Set<string>(),
  labelStartPosition: parseInt(localStorage.getItem('labelStartPosition') || '1') || 1,

  // Computed defaults
  orderCounts: { klaarmakenVerzending: 0, ingepakt: 0, bijLeverancier: 0, verzonden: 0, terugbetaald: 0, geannuleerd: 0 },
  filteredOrders: [],
  selectedOrder: null,

  fetchOrders: async () => {
    set({ isLoading: true, error: null });

    try {
      const orders = await salesforceClient.getWebshopOrders();
      const { activeTab, selectedOrderId } = get();

      set({
        ...recompute(orders, activeTab, selectedOrderId),
        isLoading: false,
        lastUpdated: new Date(),
      });
    } catch (error) {
      // In dev mode, fall back to mock data so we can test the UI
      if (import.meta.env.DEV) {
        const existing = get().orders;
        const orders = existing.length > 0 ? existing : [...MOCK_ORDERS];
        const { activeTab, selectedOrderId } = get();
        set({
          ...recompute(orders, activeTab, selectedOrderId),
          isLoading: false,
          lastUpdated: new Date(),
        });
        return;
      }
      set({
        error: error instanceof Error ? error.message : 'Kan bestellingen niet ophalen',
        isLoading: false,
      });
    }
  },

  selectOrder: (id) => {
    const orders = get().orders;
    set({
      selectedOrderId: id,
      selectedOrder: id ? orders.find((o) => o.id === id) || null : null,
      error: null,
    });
  },

  setActiveTab: (status) => {
    const orders = get().orders;
    set({
      activeTab: status,
      filteredOrders: filterForTab(orders, status),
      selectedOrderId: null,
      selectedOrder: null,
      selectedForPrintIds: new Set(),
      error: null,
    });
  },

  markAsPacked: async (orderId) => {
    set({ isUpdating: true, error: null });
    try {
      await postWebshop('/api/webshop/mark-ingepakt', { orderId });
      await get().fetchOrders();
      set({ selectedOrderId: null });
    } catch (err) {
      // In dev mode, update locally
      if (import.meta.env.DEV) {
        const orders = get().orders.map((o) =>
          o.id === orderId ? { ...o, status: 'Ingepakt' as WebshopOrderStatus } : o
        );
        const { activeTab } = get();
        set({ ...recompute(orders, activeTab, null), selectedOrderId: null });
        return;
      }
      set({ error: err instanceof Error ? err.message : 'Inpakken mislukt' });
    } finally {
      set({ isUpdating: false });
    }
  },

  markAllAsPacked: async () => {
    // Pure dropship gaat via "Doorsturen naar leverancier", niet via inpakken;
    // de server weigert die ook (NOT_OWN_STOCK).
    const klaarmakenOrders = get().orders.filter(
      (o) => o.status === 'Klaarmaken verzending' && o.fulfillmentType !== 'Dropship'
    );
    if (klaarmakenOrders.length === 0) return;

    set({ isUpdating: true, error: null });
    try {
      const uit = await postWebshop<{ ok: boolean; gelukt: number; mislukt: number; resultaten: Array<{ ok: boolean; werkOrderNummer?: string; orderId: string; message?: string }> }>(
        '/api/webshop/mark-ingepakt',
        { orderIds: klaarmakenOrders.map((o) => o.id) }
      );
      await get().fetchOrders();
      set({ selectedOrderId: null });
      if (uit && uit.mislukt > 0) {
        const fouten = uit.resultaten.filter((r) => !r.ok).map((r) => `${r.werkOrderNummer ?? r.orderId}: ${r.message ?? 'onbekend'}`);
        set({ error: `${uit.gelukt} ingepakt, ${uit.mislukt} niet: ${fouten.join('; ')}` });
      }
    } catch (err) {
      // In dev mode, update locally
      if (import.meta.env.DEV) {
        const orders = get().orders.map((o) =>
          o.status === 'Klaarmaken verzending' ? { ...o, status: 'Ingepakt' as WebshopOrderStatus } : o
        );
        const { activeTab } = get();
        set({ ...recompute(orders, activeTab, null), selectedOrderId: null });
        return;
      }
      set({ error: err instanceof Error ? err.message : 'Inpakken mislukt' });
    } finally {
      set({ isUpdating: false });
    }
  },

  createShipment: async (orderId) => {
    set({ isUpdating: true, error: null });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch('/api/create-shipment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ werkOrderId: orderId }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Onbekende fout' })) as { error?: string; detail?: string };
        const base = err.error || 'Verzendlabel aanmaken mislukt';
        const combined = err.detail ? `${base} — ${err.detail}` : base;
        throw new Error(combined);
      }

      await get().fetchOrders();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Verzendlabel aanmaken mislukt';
      set({ error: message });
      throw error instanceof Error ? error : new Error(message);
    } finally {
      set({ isUpdating: false });
    }
  },

  forwardOrder: async (orderId, to, subject, body) => {
    set({ isUpdating: true });
    // Gemengd: alleen het dropship-deel gaat naar de leverancier, de order
    // blijft in 'Klaarmaken verzending' staan voor het eigen-verzending-deel.
    const isGemengd = get().orders.find((o) => o.id === orderId)?.fulfillmentType === 'Gemengd';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      // 1) Stuur de leverancier-mail
      const mailResponse = await fetch('/api/webshop/forward-order', {
        method: 'POST',
        headers,
        body: JSON.stringify({ to, subject, body }),
      });
      if (!mailResponse.ok) {
        const err = await mailResponse.json().catch(() => ({ error: 'Onbekende fout' }));
        throw new Error(err.error || 'Email verzenden mislukt');
      }

      // 2) Flip status naar 'Bij leverancier' via WebshopDropshipService
      const flipResponse = await fetch('/api/webshop/forward-to-supplier', {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId }),
      });
      if (!flipResponse.ok) {
        // Dev-fallback
        if (import.meta.env.DEV) {
          const orders = get().orders.map((o) =>
            o.id === orderId
              ? {
                  ...o,
                  // Gemengd houdt z'n status; alleen pure Dropship gaat 'Bij leverancier'.
                  status: (isGemengd ? o.status : 'Bij leverancier') as WebshopOrderStatus,
                  dropshipDoorgestuurdDatum: new Date().toISOString(),
                  dropshipBron: 'Office-App',
                }
              : o
          );
          const { activeTab } = get();
          set({
            ...recompute(orders, activeTab, isGemengd ? orderId : null),
            isUpdating: false,
          });
          return;
        }
        // Email is wel verstuurd, maar de status flipte niet: luid melden, niet
        // stil doorgaan (10-09: twee orders bleven onzichtbaar in 'Klaarmaken').
        const err = await flipResponse.json().catch(() => ({ error: 'Onbekende fout' })) as { error?: string };
        console.error('[Webshop] forward-to-supplier flip failed na verzonden mail:', err);
        await get().fetchOrders();
        throw new Error(`Mail is verstuurd, maar de status kon niet naar 'Bij leverancier': ${err.error || 'onbekende fout'}`);
      }

      await get().fetchOrders();
      // Gemengd blijft in dezelfde tab — selectie behouden zodat de medewerker
      // de '✓ doorgestuurd'-status ziet. Pure Dropship verhuist naar 'Bij leverancier'.
      if (!isGemengd) set({ selectedOrderId: null });
    } finally {
      set({ isUpdating: false });
    }
  },

  markVerzondenViaDropship: async (orderId) => {
    set({ isUpdating: true, error: null });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch('/api/webshop/mark-dropship-verzonden', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ orderId }),
      });

      if (!response.ok) {
        // Dev-fallback
        if (import.meta.env.DEV) {
          const orders = get().orders.map((o) =>
            o.id === orderId ? { ...o, status: 'Verzonden' as WebshopOrderStatus } : o
          );
          const { activeTab } = get();
          set({
            ...recompute(orders, activeTab, null),
            selectedOrderId: null,
            isUpdating: false,
          });
          return;
        }
        const err = await response.json().catch(() => ({ error: 'Onbekende fout' })) as { error?: string };
        throw new Error(err.error || 'Markeren als verzonden mislukt');
      }

      await get().fetchOrders();
      set({ selectedOrderId: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Markeren als verzonden mislukt';
      set({ error: message });
      throw error instanceof Error ? error : new Error(message);
    } finally {
      set({ isUpdating: false });
    }
  },

  updateAddress: async (orderId, payload) => {
    set({ isUpdating: true, error: null });
    try {
      await postWebshop('/api/webshop/update-adres', { orderId, ...payload });
      await get().fetchOrders();
    } catch (err) {
      if (import.meta.env.DEV) {
        const orders = get().orders.map((o) =>
          o.id === orderId
            ? { ...o, street: payload.street, postalCode: payload.postalCode, city: payload.city, country: payload.countryCode }
            : o
        );
        const { activeTab, selectedOrderId } = get();
        set(recompute(orders, activeTab, selectedOrderId));
        set({ isUpdating: false });
        return;
      }
      set({ error: err instanceof Error ? err.message : 'Adres opslaan mislukt' });
      throw err;
    } finally {
      set({ isUpdating: false });
    }
  },

  cancelOrder: async (orderId, payload) => {
    set({ isUpdating: true, error: null });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch('/api/webshop/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ orderId, reden: payload.reden, refund: payload.refund }),
      });

      if (!response.ok) {
        // Dev-fallback: SF/Vercel niet beschikbaar — update lokaal
        if (import.meta.env.DEV) {
          const newStatus: WebshopOrderStatus = payload.refund ? 'Terugbetaald' : 'Geannuleerd';
          const orders = get().orders.map((o) =>
            o.id === orderId ? { ...o, status: newStatus } : o
          );
          const { activeTab } = get();
          set({
            ...recompute(orders, activeTab, null),
            selectedOrderId: null,
            isUpdating: false,
          });
          return;
        }
        const err = await response.json().catch(() => ({ error: 'Onbekende fout' })) as { error?: string };
        throw new Error(err.error || 'Annuleren mislukt');
      }

      await get().fetchOrders();
      set({ selectedOrderId: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Annuleren mislukt';
      set({ error: message });
      throw error instanceof Error ? error : new Error(message);
    } finally {
      set({ isUpdating: false });
    }
  },

  refresh: async () => {
    await get().fetchOrders();
  },

  toggleSelectForPrint: (id) => {
    const current = new Set(get().selectedForPrintIds);
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    set({ selectedForPrintIds: current });
  },

  selectAllForPrint: () => {
    const printableIds = get().filteredOrders
      .filter((o) => o.shippingParcelId)
      .map((o) => o.id);
    const current = get().selectedForPrintIds;
    const allSelected = printableIds.every((id) => current.has(id));
    set({ selectedForPrintIds: allSelected ? new Set() : new Set(printableIds) });
  },

  clearPrintSelection: () => {
    set({ selectedForPrintIds: new Set() });
  },

  setLabelStartPosition: (position) => {
    const pos = Math.max(1, Math.min(4, position));
    localStorage.setItem('labelStartPosition', String(pos));
    set({ labelStartPosition: pos });
  },

  startPolling: (intervalMs = 120000) => {
    const { pollingIntervalId } = get();

    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
    }

    // Fetch immediately
    get().fetchOrders();

    const id = setInterval(() => {
      get().fetchOrders();
    }, intervalMs);

    set({ pollingIntervalId: id });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },
}));
